import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, CircleAlert, Eye, EyeOff, Lock, Plus } from 'lucide-react';
import { confirmPasswordReset } from '../remote';

/** Same minimum as the first sign-in (Welcome) page and the server's rule for staff. */
const MIN_LEN = 8;

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const ResetPassword: React.FC = () => {
  const query = useQuery();
  const initialUid = query.get('uid') || '';
  const initialToken = query.get('token') || '';

  const [uid] = useState(initialUid);
  const [token] = useState(initialToken);

  // Keep the reset token out of the address bar / browser history.
  useEffect(() => {
    if (initialUid || initialToken) {
      window.history.replaceState(window.history.state, '', window.location.pathname);
    }
  }, [initialUid, initialToken]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [show, setShow] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!uid || !token) {
      setError('Missing uid or token.');
      return;
    }
    if (!password || password.length < MIN_LEN) {
      setError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const resp = await confirmPasswordReset({ uid, token, new_password: password });
      // The remote helper may hand back an error object instead of throwing.
      if (resp instanceof Error || (resp as any)?.response) throw resp;
      setDone(true);
      // Clear any stale tokens after password change to avoid confusing login state
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refresh');
      } catch {}
    } catch (e: any) {
      const data = e?.response?.data;
      const fieldError = data?.new_password ?? data?.password;
      setError(
        data?.detail ||
          (Array.isArray(fieldError) ? fieldError.join(' ') : fieldError) ||
          e?.message ||
          'Reset failed',
      );
    } finally {
      setLoading(false);
    }
  };

  const hasCase = /[a-z]/.test(password) && /[A-Z]/.test(password);
  const hasDigit = /\d/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);
  const requirements: [string, boolean][] = [
    [`At least ${MIN_LEN} characters (required)`, password.length >= MIN_LEN],
    ['Upper and lower case letters', hasCase],
    ['At least one number', hasDigit],
    ['A symbol, like ! or #', hasSymbol],
  ];

  // Strength is advisory only; the blocker is the length. Staff accounts must also pass the
  // server's password checks (the same as the first sign-in), which can still refuse a
  // common or all-digit password.
  const extras = [hasCase, hasDigit, hasSymbol].filter(Boolean).length + (password.length >= MIN_LEN ? 1 : 0);
  let score = extras;
  if (password.length >= 12 && score >= 3) score = 4;
  if (password.length < MIN_LEN) score = Math.min(score, 1);
  const LEVELS = [
    { label: 'Too short', bar: 'bg-danger', text: 'text-danger-ink' },
    { label: 'Weak', bar: 'bg-danger', text: 'text-danger-ink' },
    { label: 'Fair', bar: 'bg-warning', text: 'text-warning-ink' },
    { label: 'Good', bar: 'bg-slate', text: 'text-slate' },
    { label: 'Strong', bar: 'bg-success', text: 'text-success' },
  ];
  const level = password ? LEVELS[score] : null;
  const valid = password.length >= MIN_LEN && password === confirm;
  const match = !confirm ? null : password === confirm ? { msg: 'Passwords match', cls: 'text-success' } : { msg: 'Passwords do not match yet', cls: 'text-danger-ink' };

  const back = (
    <Link to="/login" className="mb-[26px] inline-flex min-h-[32px] items-center gap-1.5 text-[13px] font-semibold text-muted-2 no-underline hover:text-ink">
      <ArrowLeft size={18} strokeWidth={1.8} aria-hidden />Back to sign in
    </Link>
  );
  const h1 = 'm-0 font-display text-[34px] font-extrabold leading-[1.05] tracking-[-0.03em] text-ink';
  const row = 'flex min-h-[48px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5 focus-within:border-ink focus-within:ring-[3px] focus-within:ring-accent/70';
  const field = 'min-w-0 flex-1 border-0 bg-transparent p-0 text-[15px] text-ink shadow-none outline-none ring-0 focus:ring-0';

  if (done) {
    return (
      <div>
        {back}
        <div role="status">
          <div className="mb-[22px] grid h-14 w-14 place-items-center rounded-[18px] bg-success-bg text-success"><Check size={26} strokeWidth={2.4} aria-hidden /></div>
          <h1 className={h1}>Password updated</h1>
          <p className="mb-0 mt-2.5 text-[15px] text-muted">You have been signed out on other devices. Sign in with your new password to continue.</p>
          <Link to="/login" className="mt-[22px] flex min-h-[50px] w-full items-center justify-center gap-2 rounded-[12px] bg-navy text-[15px] font-semibold text-white no-underline hover:text-accent">
            Go to sign in<ArrowRight size={18} strokeWidth={1.8} aria-hidden />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {back}
      <h1 className={h1}>Set a new password</h1>
      <p className="mb-[30px] mt-2.5 text-[15px] text-muted">Choose a password you have not used before.</p>

      <form onSubmit={onSubmit}>
        <label htmlFor="reset-password" className="mb-2 block font-semibold text-ink">New password</label>
        <div className={row}>
          <Lock size={18} strokeWidth={1.8} className="text-muted" aria-hidden />
          <input
            id="reset-password"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder={`At least ${MIN_LEN} characters`}
            aria-describedby="reset-reqs"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(null); }}
            className={field}
          />
          <button type="button" onClick={() => setShow(!show)} aria-label={show ? 'Hide passwords' : 'Show passwords'}
            className="grid h-9 w-9 place-items-center rounded-[8px] bg-transparent p-0 text-muted hover:bg-track">
            {show ? <EyeOff size={18} strokeWidth={1.8} aria-hidden /> : <Eye size={18} strokeWidth={1.8} aria-hidden />}
          </button>
        </div>
        <div className="mt-3 flex gap-1.5" aria-hidden="true">
          {[1, 2, 3, 4].map((k) => (
            <span key={k} className={`h-1.5 flex-1 rounded-[3px] transition-colors ${level && k <= score ? level.bar : 'bg-line'}`} />
          ))}
        </div>
        <div className="mt-1.5 flex justify-between text-xs">
          <span className="text-muted">Strength</span>
          <span aria-live="polite" className={`font-semibold ${level ? level.text : 'text-muted'}`}>{level ? level.label : '—'}</span>
        </div>
        <ul id="reset-reqs" className="m-0 mt-3.5 grid list-none gap-2 p-0 text-[13px]" aria-label="Password requirements">
          {requirements.map(([label, met]) => (
            <li key={label} className={`flex items-center gap-2.5 ${met ? 'text-success' : 'text-muted'}`}>
              <span className={`grid h-5 w-5 flex-none place-items-center rounded-full ${met ? 'bg-success-bg text-success' : 'bg-track text-muted'}`}>
                {met ? <Check size={12} strokeWidth={3} aria-hidden /> : <Plus size={12} strokeWidth={3} aria-hidden />}
              </span>
              {label}<span className="sr-only">{met ? ' (met)' : ' (not met)'}</span>
            </li>
          ))}
        </ul>

        <label htmlFor="reset-confirm" className="mb-2 mt-[22px] block font-semibold text-ink">Confirm password</label>
        <div className={row}>
          <Lock size={18} strokeWidth={1.8} className="text-muted" aria-hidden />
          <input
            id="reset-confirm"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Type it again"
            aria-describedby="reset-match"
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); setError(null); }}
            className={field}
          />
        </div>
        <div id="reset-match" aria-live="polite" className={`mt-2 flex min-h-[20px] items-center gap-2 text-[13px] ${match ? match.cls : 'text-muted'}`}>{match?.msg}</div>
        {error && (
          <div role="alert" className="mt-2.5 flex items-start gap-2 text-[13px] text-danger-ink">
            <CircleAlert size={16} strokeWidth={1.8} className="mt-px flex-none" aria-hidden />{error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          aria-disabled={!valid || undefined}
          className={`mt-[22px] flex min-h-[50px] w-full items-center justify-center gap-2 rounded-[12px] bg-navy text-[15px] font-semibold text-white hover:text-accent disabled:opacity-[0.55] ${valid ? '' : 'opacity-[0.55]'}`}
        >
          {loading ? 'Saving…' : 'Reset password'}
          <ArrowRight size={18} strokeWidth={1.8} aria-hidden />
        </button>
      </form>
    </div>
  );
};

export default ResetPassword;
